import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { UserSettingsService } from './user-settings.service';
import { UserSettingStore } from './user-setting-store.base';

/**
 * What a concrete attribute-preset store is: which `/v2/userSettings` key it caches, which
 * `data-*` attribute it stamps on <html>, which boot mirror the index.html script reads for it —
 * key AND web-storage area, since they are not all localStorage (see `mirrorArea`) — and which OS
 * media query its 'system' preset follows, if it follows one at all (`mediaQuery` is nullable;
 * `display.cvd` has no OS signal to consult).
 */
export interface AttributePresetConfig {
  /** Key in the /v2/userSettings blob, and the discriminator on the shared BroadcastChannel. */
  settingsKey: string;
  /** The `data-*` attribute written on <html>. */
  attributeName: string;
  /** Storage key holding the RESOLVED value, read by the inline script in index.html. */
  mirrorKey: string;
  /**
   * WHICH web-storage area the mirror lives in, and it is a PRIVACY decision rather than a
   * lifetime one.
   *
   * `'local'` is the ordinary answer and what the flash-prevention story assumes: the mirror
   * outlives the tab, so a user who closed the app yesterday still gets their theme on the first
   * paint today.
   *
   * `'session'` is for a preset whose applied value DISCLOSES SOMETHING ABOUT ITS OWNER — today
   * `display.cvd` alone. `identityScoped()` already clears such a mirror on a logout and on a
   * cross-tab switch, but neither event fires when the operator simply closes the tab or quits
   * the browser at the end of a shift, which is the most ordinary way this app is left: a
   * `localStorage` mirror then survives, and the boot script re-stamps it for whoever opens the
   * app next on a shared counter machine. sessionStorage dies with the tab, is not visible to a
   * tab the next operator opens, and still survives a RELOAD of the same tab — which is the only
   * cold-load flash the mirror was ever buying for this preset, since its value is cleared on
   * every identity change anyway.
   *
   * SURVIVING A RELOAD IS WHY THE MIRROR CARRIES ITS OWNER. It is the case neither half covers:
   * the tab is the same, so the mirror lives, but the identity behind the shared refresh cookie
   * need not be. An identity-scoped mirror is therefore stamped `"<userId>:<value>"` and cleared
   * on the first emission that names somebody else — see MIRROR_OWNER_SEPARATOR and
   * `onIdentityPresent`. A store that keeps its value writes no owner, deliberately.
   *
   * Two residues, stated rather than implied. Reopening a closed tab (Ctrl+Shift+T) and a
   * browser's own crash-restore both restore sessionStorage; both are a deliberate act by
   * whoever is at the machine, and both restore the whole page with it — and the owner stamp
   * covers the case where the identity has moved on since. And a SECOND tab opened
   * by the same user mid-session has no mirror, so it paints the ordinary palette until the
   * settings GET resolves — which is the flash `identityScoped()` already accepts by name.
   *
   * Clearing on `pagehide` instead was considered and is incoherent: `pagehide` fires on a
   * RELOAD too, so the mirror would never survive to be read and the whole mechanism would be
   * dead weight carrying a boot script and a gate.
   */
  mirrorArea: 'local' | 'session';
  /**
   * The OS signal this preset follows, or `null` where there is none.
   *
   * NULLABLE BECAUSE ONE PRESET GENUINELY HAS NO SIGNAL TO FOLLOW: `display.cvd` has no 'system'
   * value, because no platform exposes a colour-vision media feature and a browser that did would
   * be leaking a medical fact its user never typed. Passing a query that can never match — `'not
   * all'` — would work by accident and read as an oversight; passing `null` says it.
   * Every `this.media?.` below already tolerates the absence, since `matchMedia` can be missing in
   * a test document too.
   */
  mediaQuery: string | null;
}

/**
 * The mirror value meaning "this user has explicitly pinned the OFF state" — as opposed to the
 * key being ABSENT, which means "nothing was ever pinned; resolve from the OS".
 *
 * The distinction is load-bearing for any preset whose off state is the attribute's ABSENCE
 * (contrast). Removing the mirror for an explicit off would send the boot script down its OS
 * branch on the next cold load, so a user who deliberately chose normal contrast on a machine
 * that prefers more would get a high-contrast flash on every single load — the same class of bug
 * as the boot-mirror clobber, arriving through the value rather than through the timing. For a
 * preset whose resolve() never returns null (theme) this constant is simply never written.
 */
export const PINNED_OFF_MIRROR = 'off';

/**
 * Separator between the owner id and the resolved value in an IDENTITY-SCOPED mirror.
 *
 * A mirror written by a store whose applied state must not outlive its owner is stamped
 * `"<userId>:<resolved>"` — `"42:protan"` — and one written by a store that KEEPS its value across
 * an identity change carries no owner at all. That asymmetry is the point: a theme is a property of
 * the DEVICE and is meant to be read by whoever sits down next, and stamping an owner on it would
 * break the flash prevention it exists for.
 *
 * WHY THE OWNER IS THERE. `identityScoped()` clears the mirror on a logout and on a cross-tab
 * switch, which covers every way an identity is REPLACED — and the boot script re-stamps from the
 * mirror before any of those hooks can run. `mirrorArea: 'session'` closes the "shift simply ended"
 * half by dying with the tab, and deliberately leaves the mirror surviving a RELOAD of the same tab,
 * which is the only cold-load flash it was ever buying. A reload is exactly where the two halves
 * meet and neither one holds: the tab is the same, so the mirror survives, but the identity behind
 * the shared refresh cookie need not be — B can have signed in on another tab first. The store then
 * came back with `activeIdentity` null, could not tell A's stamp from B's, and painted A's palette
 * under B until B's settings GET resolved, or for good if it errored (`rearmLoad()` only re-arms
 * for the next /app entry). Recording the owner is what makes the question answerable at all: see
 * `onIdentityPresent`.
 */
const MIRROR_OWNER_SEPARATOR = ':';

/**
 * The owner a raw mirror value records, or `null` when it records none — an unstamped mirror from a
 * store that keeps its value, or one written by a build that predates the stamp.
 *
 * The head must be ALL DIGITS, and the separator must not be the first character. A resolved value
 * in this vocabulary never contains a colon ('light', 'dark', 'more', 'off', a deficiency name), so
 * this cannot mis-read one — and a value that somehow did would answer `null` (unattributed) rather
 * than half a value, which is the direction that fails closed for an identity-scoped store.
 */
export function mirrorOwnerOf(raw: string): number | null {
  const cut = raw.indexOf(MIRROR_OWNER_SEPARATOR);
  if (cut <= 0) return null;
  const head = raw.slice(0, cut);
  return /^[0-9]+$/.test(head) ? Number(head) : null;
}

/** The resolved value a raw mirror value carries, with any owner stamp removed. An unattributed
 *  mirror is its own value, which is what keeps the theme and the contrast readings unchanged. */
export function mirrorValueOf(raw: string): string {
  return mirrorOwnerOf(raw) === null ? raw : raw.slice(raw.indexOf(MIRROR_OWNER_SEPARATOR) + 1);
}

/**
 * Shared runtime for the display presets that are PAINTED THROUGH AN ATTRIBUTE ON <html> rather
 * than consumed through a pipe: the theme, and the contrast level.
 *
 * Everything here was extracted VERBATIM from ThemeService — the comments included, because each
 * of them records a regression that shipped and was fixed: the boot-mirror clobber, the
 * captured-at-construction `bootStamped`, the emit-before-persist ordering in setPreset(), the
 * promote-an-equal-default branch in adopt(), and the zone re-entry on the media query. A second
 * preset copying that shape by hand would have re-derived every one of those bugs, so the shape
 * is the base class and the differences are the four config strings plus two methods.
 *
 * The cross-tab channel, the once-per-identity load, and the optimistic write are inherited from
 * UserSettingStore. This layer adds only the side effects that make a preset VISIBLE: stamping
 * the attribute, and mirroring the RESOLVED value into web storage (which area, and why it is a
 * privacy decision rather than a lifetime one, is `AttributePresetConfig.mirrorArea`).
 *
 * Why the mirror exists: the base loads preferences only once the user is fully authenticated AND
 * on an /app route. Everything before that — first paint, the login page, the 2FA wizard —
 * renders on the default. A light-theme (or high-contrast) user would see the default flash on
 * every cold load. The inline script in index.html reads the mirror and stamps the attribute
 * before the Angular bundle parses, so there is nothing to flash.
 *
 * The mirror is a CACHE; the server is the source of truth. When they disagree, load() wins and
 * rewrites it.
 *
 * `@Injectable()` (no `providedIn`) is required because this base uses Angular features (DI'd
 * constructor params + the OnDestroy hook); the concrete stores carry the real
 * `providedIn: 'root'` registration and their own constructors.
 */
@Injectable()
export abstract class AttributePresetStore<P extends string, R extends string | null = string | null>
  extends UserSettingStore
  implements OnDestroy
{
  /**
   * Coerce an untrusted value (a settings-blob entry, a cross-tab broadcast payload) to a known
   * preset.
   *
   * A METHOD, not a field holding a function: a subclass field initializer runs AFTER super(), so
   * a `protected coerce = coerceContrast` would still be undefined for anything the base
   * constructor calls. Prototype methods exist from the first line of super(), which is the whole
   * reason the two hooks are shaped this way.
   */
  protected abstract coerce(v: unknown): P;

  /**
   * Map a stored preset to the value actually painted, given the current state of the OS query.
   *
   * `null` = REMOVE the attribute and (for a default preset) the mirror: absent is a state, not a
   * value. The contrast tokens match on the attribute's PRESENCE, so writing `data-contrast="normal"`
   * would look right and paint nothing. A subclass whose attribute is always present (theme)
   * simply never returns null.
   */
  protected abstract resolve(preset: P, mediaMatches: boolean): R;

  /**
   * Read a raw mirror value back as the resolution the BOOT SCRIPT painted from it, or `undefined`
   * when this store does not recognise it.
   *
   * This is the boot script's whitelist, restated in TypeScript, and the two must agree: the
   * script only ever stamps values it recognises, so a junk or tampered mirror (localStorage is
   * writable by any script on the origin) paints nothing and must not be reported here either —
   * `undefined` sends the seed back to the OS-derived resolution, which is exactly the branch the
   * script itself takes. `null` is a legitimate ANSWER, not a miss: it is what PINNED_OFF_MIRROR
   * means, which is why this returns `R | undefined` rather than `R | null`.
   */
  protected abstract resolvedFromMirror(mirror: string): R | undefined;

  protected readonly preset: BehaviorSubject<P>;
  readonly preset$: Observable<P>;

  get currentPreset(): P {
    return this.preset.value;
  }

  /** The RESOLVED value, for a settings preview that has to show what the preset actually does
   *  rather than what the user picked ('system' shows nothing on its own). */
  private readonly resolved: BehaviorSubject<R>;
  readonly resolved$: Observable<R>;

  /**
   * The OS-level preference, watched so that 'system' is LIVE rather than resolved once.
   *
   * Assigned in the constructor BODY, not at the declaration: `doc` is a parameter property and
   * the zone is the base's, so an initialiser here would depend on assignment order rather than on
   * something the code states. `matchMedia` is optional-chained because jsdom-style test hosts and
   * very old browsers do not provide it, and a missing media query must degrade to "not matching"
   * rather than throw at construction — this service runs before anything else can catch.
   */
  private readonly media: MediaQueryList | null;
  private readonly onMediaChange: () => void;

  /**
   * The three mirror operations, one per area, branching INSIDE each method rather than resolving
   * an area and handing it back.
   *
   * TWO REASONS, AND NEITHER IS STYLE. CLAUDE.md's storage section forbids a `Storage` value
   * crossing a call boundary in either direction: Chrome with "block all cookies", and any
   * sandboxed iframe without `allow-same-origin`, throw `SecurityError` from the `window.localStorage`
   * property GETTER itself, so a resolved reference is an exception waiting at whichever line
   * produced it. A helper RETURNING one is the same hazard as a helper taking one, mirrored — here
   * it would throw inside `apply()`'s own try, so it happens to be safe, which is exactly the kind
   * of accident that stops being true when somebody calls it from somewhere else.
   *
   * And each access is spelled out in full — `view.localStorage.getItem`, `view.sessionStorage.setItem`
   * — so this file still ANSWERS the audit grep CLAUDE.md documents for the storage rule,
   * `grep -rn "localStorage\.\|sessionStorage\." src --include=*.ts`. A single `area(view)`
   * helper returning the area reads better and drops the file out of that grep entirely: raw
   * storage use that the one command meant to find it can no longer see.
   *
   * See `AttributePresetConfig.mirrorArea` for which preset uses which area, and why.
   */
  private mirrorWrite(view: Window, value: string): void {
    if (this.cfg.mirrorArea === 'session') {
      view.sessionStorage.setItem(this.cfg.mirrorKey, value);
    } else {
      view.localStorage.setItem(this.cfg.mirrorKey, value);
    }
  }

  private mirrorRemove(view: Window): void {
    if (this.cfg.mirrorArea === 'session') {
      view.sessionStorage.removeItem(this.cfg.mirrorKey);
    } else {
      view.localStorage.removeItem(this.cfg.mirrorKey);
    }
  }

  /** A mirror value as it goes to storage: owner-stamped for an identity-scoped store, bare for
   *  one that keeps its value across an identity change. See MIRROR_OWNER_SEPARATOR. */
  private stamped(owner: number | null, value: string): string {
    return owner === null ? value : `${owner}${MIRROR_OWNER_SEPARATOR}${value}`;
  }

  private mirrorRead(view: Window): string | null {
    return this.cfg.mirrorArea === 'session'
      ? view.sessionStorage.getItem(this.cfg.mirrorKey)
      : view.localStorage.getItem(this.cfg.mirrorKey);
  }

  /**
   * False until the FIRST authoritative preset arrives — a server load, a cross-tab broadcast, or
   * the user picking one. Until then the subject only holds its DEFAULT, and applying
   * that would overwrite the stamp the index.html boot script made from the mirror — which is
   * exactly the bug this flag closes: on a machine whose OS prefers dark, the constructor's
   * replay stamped dark over the mirror's light AND rewrote the mirror to dark, so every
   * subsequent cold load booted dark and flashed to light only when the settings request
   * returned. The mirror poisoned itself on the very first visit.
   */
  private hydrated = false;

  /**
   * Whether the index.html boot script had ALREADY expressed this user's remembered answer when
   * the service was constructed. Captured once, at construction: apply() cannot test the attribute
   * live, because the service's own first stamp would then satisfy the test and block every later
   * pre-hydration re-apply (a 'system' user's OS flipping before login).
   *
   * It consults the MIRROR as well as the attribute, and the mirror half is not belt-and-braces —
   * it is the only half that works for a preset whose OFF state is the attribute's ABSENCE. The
   * contrast boot script stamps nothing in both of its off states, so an attribute-only test reads
   * "no opinion", deference never engages, and the constructor's 'system' replay applies straight
   * over a user who had PINNED normal contrast: the high-contrast flash on every cold load that
   * PINNED_OFF_MIRROR exists to prevent, plus the sentinel overwritten 'off' -> 'more' (or removed
   * outright) at construction, self-healing only at hydration — so a tab closed before reaching
   * /app, or a settings load that errors, carries the poisoned mirror into the NEXT load. The
   * theme is unaffected: its script stamps on every branch, including the catch, so its attribute
   * and its mirror always agree in presence.
   */
  private bootStamped = false;

  /**
   * The identity whose value is currently APPLIED, or null while nobody is signed in.
   *
   * Tracked rather than inferred from a logout, because an identity can be replaced WITHOUT one:
   * a cross-tab switch A -> B rides the shared refresh cookie, so isAuthenticated never drops and
   * UserSettingStore.init()'s `!userId` branch — the only caller of onLoggedOut() — is never
   * taken. AvatarService keeps the same field, for the same event, one store over.
   *
   * Assigned from `onIdentityPresent`, which the base calls on the identity itself rather than on
   * a ready session — see that override for the enrollment-scoped switch that assigning it from
   * `load()` cannot see.
   */
  private activeIdentity: number | null = null;

  constructor(
    userSettings: UserSettingsService,
    store: Store,
    zone: NgZone,
    private readonly doc: Document,
    private readonly cfg: AttributePresetConfig,
    private readonly defaultPreset: P,
  ) {
    super(userSettings, store, zone);
    this.preset = new BehaviorSubject<P>(defaultPreset);
    this.preset$ = this.preset.asObservable();
    // Apply from the subject rather than from each mutation site, so load(), onBroadcast() and
    // setPreset() cannot drift apart. BehaviorSubject replays its current value, so this also
    // performs the initial stamp — before any HTTP, before login — EXCEPT when the boot script
    // already stamped from the mirror; apply() defers to that until something authoritative
    // arrives (see `hydrated`).
    const bootMirror = this.readMirror();
    this.bootStamped = this.doc.documentElement.hasAttribute(cfg.attributeName) || bootMirror !== null;
    this.media = cfg.mediaQuery === null ? null : this.doc.defaultView?.matchMedia?.(cfg.mediaQuery) ?? null;
    // Seed the preview from the MIRROR, not from resolve(default, matches): while apply() is
    // deferring to the boot script, what is painted is what the script read out of the mirror, and
    // the subject holds only its default. Seeding from the default would tell a Preferences
    // preview that a pinned-off user is on whatever the OS says. An unrecognised mirror value
    // falls back to the OS-derived resolution, which is the branch the boot script takes for it.
    // The VALUE half of the mirror, never the raw string: an identity-scoped mirror carries an
    // owner stamp (see MIRROR_OWNER_SEPARATOR), and handing `"42:protan"` to a subclass whose
    // whitelist expects `"protan"` would answer "unrecognised" for every attributed mirror ever
    // written — seeding the preview from the OS-derived default while the boot script paints the
    // real thing. `mirrorValueOf` is the identity for an unattributed one, so the two stores that
    // keep their value read exactly what they read before.
    const seeded = bootMirror === null ? undefined : this.resolvedFromMirror(mirrorValueOf(bootMirror));
    this.resolved = new BehaviorSubject<R>(
      seeded === undefined ? this.resolve(defaultPreset, this.media?.matches ?? false) : seeded,
    );
    this.resolved$ = this.resolved.asObservable();
    this.onMediaChange = () => zone.run(() => this.apply(this.preset.value));
    this.preset.subscribe(preset => this.apply(preset));

    // 'system' means "follow the OS", and an OS preference can change while the tab is open — at
    // dusk, on most desktops, automatically. Resolving it once at boot would strand such a user on
    // the value their machine had at load time, which is the one behaviour the preset promises not
    // to have. The listener re-applies rather than re-reads a stored value, so a user on a pinned
    // preset is unaffected: resolve() ignores the OS for those.
    //
    // zone.run because a matchMedia callback is NOT zone-patched: without it the attribute would
    // change with no change-detection pass behind it, the same trap UserSettingStore documents for
    // BroadcastChannel.
    this.media?.addEventListener('change', this.onMediaChange);
  }

  override ngOnDestroy(): void {
    // The base closes the BroadcastChannel; this closes the media query. Both, in that order.
    super.ngOnDestroy();
    this.media?.removeEventListener('change', this.onMediaChange);
  }

  setPreset(value: P): void {
    // NOT adopt(): adopt emits, and the base's setPref refuses a value equal to the subject's —
    // so emitting first made setPref an early return and the pick was never PERSISTED or
    // broadcast. The symptom was exact: the tab repainted (the subject drove apply), no request
    // left, sibling tabs never heard, and the next reload came back on the server's old answer.
    // Order matters: hydrate, promote an already-equal default (setPref won't re-emit it), then
    // let setPref do the emit + persist + broadcast it owns.
    this.hydrated = true;
    if (value === this.preset.value) {
      this.apply(value);
    }
    this.setPref(this.preset, this.cfg.settingsKey, value, this.defaultPreset);
  }

  /**
   * A DIFFERENT identity is taking over this tab, which is NOT a logout and does not pass through
   * one: a cross-tab switch A -> B keeps the shared refresh cookie, so isAuthenticated never drops
   * and onLoggedOut() is never called (UserSettingStore.init()'s own comments say so). Without
   * this a store whose applied state must not outlive its owner keeps painting A's answer, under
   * B, for the whole of B's GET — and for good if that GET errors, since rearmLoad() only re-arms
   * the retry for the next /app entry.
   *
   * IT HANGS OFF `onIdentityPresent` AND NOT `load()`, and the difference is a whole class of
   * missed switch rather than a few milliseconds. `load()` runs only once the base considers the
   * session READY, which is `isAuthenticated && onApp && !enrollmentRequired` — so an incoming
   * user whose token is forced-2FA-enrollment-scoped never reaches it: `leaveAfterSwitch(true)`
   * navigates the tab to the enrollment wizard, readiness stays false on both counts, and the
   * clear below never runs at all. The outgoing operator's palette and mirror then sit under the
   * incoming one for the whole of their time in the wizard, and for ever if they abandon it —
   * which is the shared-counter disclosure CvdService's `identityScoped()` exists to close,
   * surviving on the one path nobody would look at. `onIdentityPresent` fires on the identity
   * itself, before any readiness test.
   *
   * Called on every emission carrying a userId, so it acts only on a CHANGE.
   *
   * THE SECOND BRANCH IS THE RELOAD, and without it the first one has an unstated hole on exactly
   * the path `mirrorArea: 'session'` was chosen for. `activeIdentity` is null after every page
   * load, so "the identity changed" cannot be asked at all on the first emission — and the boot
   * script has already stamped, from a `sessionStorage` mirror that survives a reload BY DESIGN.
   * Reload a tab whose shared refresh cookie has since become B's (B signed in on another tab; or
   * `ChunkErrorRecoveryService` reloaded this one) and A's palette is painted under B for the whole
   * of B's settings GET, and for good if it errors. That is the shared-counter disclosure
   * `identityScoped()` exists to close, surviving on the one path the first branch is blind to.
   *
   * So an identity-scoped store asks whose stamp it is, which is what the owner in the mirror is
   * for. A mirror belonging to somebody else is cleared like any other identity change; so is an
   * UNATTRIBUTED one, because an owner that cannot be read is an owner that cannot be matched and
   * this is the direction that fails closed. In practice the unattributed case is a mirror written
   * by a build that predates the stamp, and it costs that tab one flash, once.
   */
  protected override onIdentityPresent(userId: number): void {
    if (this.activeIdentity !== null && this.activeIdentity !== userId) {
      this.onIdentityGone();
    } else if (this.activeIdentity === null && this.identityScoped() && this.bootStampIsForeign(userId)) {
      this.onIdentityGone();
    }
    this.activeIdentity = userId;
  }

  /**
   * Whether the mirror on disk — i.e. what the boot script painted from — belongs to somebody other
   * than `userId`. An ABSENT mirror is not foreign: there is nothing stamped and nothing to clear,
   * which is what keeps a brand-new user's first login from resetting over a boot script that never
   * ran.
   *
   * Read LIVE rather than captured at construction, because `apply()` keeps the mirror in step with
   * what is applied: after a logout-then-login inside one page load the constructor's reading is two
   * identities stale, while the live one always describes the value currently painted.
   */
  private bootStampIsForeign(userId: number): boolean {
    const raw = this.readMirror();
    return raw !== null && mirrorOwnerOf(raw) !== userId;
  }

  protected load(userId: number): void {
    // No identity bookkeeping here: onIdentityPresent() above owns it, and has already run for
    // this userId in the same emission — see its note for why doing it here instead misses the
    // switch that matters most.
    this.userSettings
      .getAllSharedStrict(userId)
      .pipe(take(1))
      .subscribe({
        next: settings => {
          // A response for a SUPERSEDED identity must not adopt. It would re-stamp the attribute
          // and re-write the mirror onIdentityGone() has just cleared — the outgoing user's
          // answer landing on the login page, or under the incoming one — and it is reachable in
          // both directions: a GET in flight at logout, and a GET issued as A that resolves after
          // the switch or simply out of order with B's own. It is the whole point for a preset
          // that discloses something about its owner, and it is NOT invisible for the two that
          // keep their value — see resetToDefault() for what it costs them and why that is the
          // trade taken.
          if (userId !== this.activeIdentity) return;
          this.adopt(this.coerce(settings[this.cfg.settingsKey]));
        },
        error: () => this.rearmLoad(userId),
      });
  }

  /**
   * The identity went away by LOGOUT. Kept in step with the switch branch in load(): both clear
   * the tracked identity and run the same hook, so a subclass has ONE place to say what must not
   * outlive its owner. A subclass that overrode this instead would cover the logout and miss the
   * switch, which is the bug the pair exists to prevent — override onIdentityGone(), and if you
   * must override this one, call super.
   */
  protected override onLoggedOut(): void {
    this.activeIdentity = null;
    this.onIdentityGone();
  }

  /**
   * NO SESSION AT STARTUP — the one boot a live session never produces, and the one path with no
   * identity change for onIdentityPresent() or onLoggedOut() to notice.
   *
   * Reaching this hook is itself the finding: `AuthRefreshService.bootstrap()` is an AWAITED
   * APP_INITIALIZER (it returns a Promise) and every store's init() is called from AppComponent, so
   * a tab whose refresh cookie is still good has already dispatched APPLY_SESSION with its real
   * userId by the time UserSettingStore.init() subscribes — its first emission carries that
   * identity and comes nowhere near here. An emission that DOES arrive here is a tab holding a boot
   * stamp with nobody left to own it. app.module.spec.ts gates that ordering, because the safety of
   * clearing here is that ordering and nothing else.
   *
   * The disclosure it closes is the one the rest of this class already closes everywhere else: the
   * mirror survives a reload BY DESIGN (sessionStorage, for the one preset that is identity-scoped)
   * while the session behind the shared refresh cookie need not, so the outgoing operator's palette
   * stayed painted on the login page the next one is looking at — and their mirror stayed readable
   * to any script on the origin — for the whole of that visit to /auth.
   *
   * No identity bookkeeping: `activeIdentity` is assigned only by onIdentityPresent(), which cannot
   * have run, so it is already null. Gated by identityScoped() like every other caller of
   * onIdentityGone(), so the theme and the contrast level keep their stamp and their cold-load
   * flash prevention is untouched.
   */
  protected override onNoIdentityAtStartup(): void {
    this.onIdentityGone();
  }

  /**
   * Whether this store's APPLIED state must not outlive the identity that chose it. `false` by
   * default: the theme and the contrast level are deliberately KEPT (a colour scheme is not an
   * identity leak, and dropping the mirror reintroduces the cold-load flash it exists to prevent
   * — see ThemeService). A store whose applied state discloses something about its owner answers
   * `true` — see CvdService, which is the only one that does.
   *
   * IT IS ONE FACT WITH TWO CONSUMERS, and that is the whole reason it is a predicate rather than
   * an overridden hook. Clearing on the way out (onIdentityGone) and refusing to adopt on the way
   * back in (onBroadcast) are two halves of the same claim, and a subclass that overrode one and
   * not the other would clear the value and then have a late cross-tab message put it straight
   * back — the same "override one, miss the other" failure the onLoggedOut/onIdentityGone pairing
   * above already exists to prevent, one layer further in.
   *
   * A METHOD, not a readonly field, and the constructor is the sharper half of the reason.
   *
   * It is read from onBroadcast, which the base's own contract says can run before the subclass
   * constructor body (a channel delivery is a task — see handlesKey below for why `cfg` is
   * nevertheless safe there). It is ALSO read SYNCHRONOUSLY FROM THIS CLASS'S OWN CONSTRUCTOR:
   * `this.preset.subscribe(...)` replays the BehaviorSubject immediately, which runs `apply()` and
   * so `writeMirror()`, and that asks this question — strictly before any subclass field
   * initializer has run, and before the subclass constructor body starts. The broadcast path is a
   * hazard a future delivery might hit; this one is taken on every construction of every store.
   *
   * So a field initializer would not merely be at risk of being unassigned, it would BE unassigned:
   * an implementation written `return this.someFlag` reads `undefined`, takes the non-scoped branch,
   * and writes an unstamped mirror that `bootStampIsForeign` can then never attribute to anybody —
   * failing OPEN, on the one store whose whole point is failing closed. A prototype method is always
   * assigned, which is why this must stay one and why an override must not depend on instance state.
   */
  protected identityScoped(): boolean {
    return false;
  }

  /**
   * The identity whose value is currently applied has gone — logged out, or replaced in place by
   * a cross-tab switch. Resets an identity-scoped store to its default, stamping and clearing the
   * mirror as it goes; a no-op for the two that keep their value. Override `identityScoped()`
   * rather than this, so the broadcast guard moves with it.
   */
  protected onIdentityGone(): void {
    if (this.identityScoped()) {
      this.resetToDefault();
    }
  }

  /**
   * READS `this.cfg`, a parameter property, which UserSettingStore's own doc on this hook forbids:
   * "must not touch subclass instance fields — it can run before the subclass constructor body via
   * onmessage". The exemption is specific and worth stating rather than rediscovering. The hook is
   * called from `channel.onmessage`, and a BroadcastChannel delivery is a TASK: it cannot be
   * dispatched synchronously inside the constructor that opened the channel, so `cfg` is always
   * assigned by the time either of these runs. Pre-refactor ThemeService compared against a module
   * constant and could not reach the hazard at all — that is what changed, not the guarantee.
   * Anything here that is NOT a constructor parameter (a subclass field initializer, say) still
   * falls under the base's rule.
   */
  protected override handlesKey(key?: string): boolean {
    return key === this.cfg.settingsKey;
  }

  /**
   * See handlesKey above for why reading `this.cfg` from a channel hook is safe here.
   *
   * THE IDENTITY GUARD IS THE OTHER HALF OF onIdentityGone(), and it is scoped to the stores that
   * clear rather than applied to all three. A broadcast is AUTHORITATIVE — adopt() ends the boot
   * deference and rewrites the mirror — so one arriving with no identity in place re-stamps the
   * attribute and re-writes the mirror the clear has just removed, i.e. the outgoing user's
   * palette back on the login page. It is reachable without a race being exotic: persist()
   * broadcasts only once the write CONFIRMS, which is asynchronous to a teardown, so a sibling
   * tab's pick made just before the logout lands just after it. AvatarService guards its own
   * handler on exactly this, one field over.
   *
   * THE RESIDUE, stated rather than implied: this closes the LOGGED-OUT window and not the SWITCH
   * one. On an A -> B hand-off the tracked identity is reassigned rather than nulled, so a message
   * still in flight from A is adopted under B — and nothing here can tell it from B's own pick in
   * a third tab, because the payload carries a key and a value and no identity at all. Closing it
   * means stamping the sender's userId into the broadcast, which is a change to the shared
   * channel's contract for every store on it, not a guard in this method.
   *
   * The two stores that KEEP their value are deliberately left unguarded: for them a message with
   * no identity in place is simply a later authoritative answer, and dropping it would have a tab
   * still booting ignore a sibling's theme pick for no gain.
   */
  protected onBroadcast(data: { key?: string; value?: unknown }): void {
    if (data?.key !== this.cfg.settingsKey) return;
    if (this.identityScoped() && this.activeIdentity === null) return;
    this.adopt(this.coerce(data.value));
  }

  /**
   * Take an AUTHORITATIVE preset — a server load, a cross-tab broadcast, the user's pick — ending
   * the boot-script deference. The equality branch matters: the base's emit() deduplicates, so a
   * value equal to the subject's default would never re-run apply(), and a boot stamp that
   * disagrees with what that value resolves to (a mirror gone stale while logged out, say the OS
   * flipped) would stand for the life of the tab. Calling apply() directly promotes the default
   * from "assumed" to "confirmed": same value, but now it stamps and refreshes the mirror.
   */
  private adopt(value: P): void {
    this.hydrated = true;
    if (value === this.preset.value) {
      this.apply(value);
    } else {
      this.emit(this.preset, value);
    }
  }

  /**
   * Go back to the default preset, stamping and clearing the mirror as it goes — for a store
   * whose APPLIED state must not outlive the identity that chose it (see the `identityScoped()`
   * predicate below, and CvdService's override of it).
   *
   * It is `adopt(default)` and not `emit(preset, default)`, and the difference is the whole
   * reason it lives on the base rather than in the subclass: `apply()` is private and DEFERS to
   * the boot stamp until `hydrated` is set, so a store that reset its subject directly would
   * leave the previous identity's attribute and mirror in place on every path where nothing has
   * hydrated yet — a logout while the settings GET is still in flight, with the boot script's
   * stamp from the previous identity's mirror still on <html>. Going through adopt() promotes the
   * default from "assumed" to "confirmed", which is what actually removes both.
   *
   * A load that RESOLVES after the identity is gone cannot undo it: load() drops a response whose
   * identity has been superseded, so a GET in flight at logout — or one issued as A that lands
   * after the switch to B — no longer re-stamps the attribute and re-writes the mirror this has
   * just cleared.
   *
   * THAT GUARD IS ON THE BASE, AND IT IS NOT INERT FOR THE TWO STORES THAT KEEP THEIR VALUE — which
   * is what this note claimed for one commit, and the claim was wrong. Before it, a superseded
   * response WAS adopted by the theme and the contrast, and adopting does two things: it repaints
   * (the outgoing user's theme landing on the login page — a defect the guard removes) and it
   * writes the mirror (which on a tab that has never had one is the only thing that ever would).
   * So the guard costs them exactly one narrow, self-healing case: a session whose settings GET
   * resolves only AFTER teardown now leaves the mirror unwritten, and that tab pays the cold-load
   * flash the mirror exists to prevent until the next login that completes writes it.
   *
   * Taken deliberately rather than overlooked. The alternative — scoping the guard to
   * `identityScoped()` — buys back that one mirror write at the price of keeping the repaint, and
   * a preference belonging to somebody who has logged out has no business repainting the screen
   * they logged out to. (`contrast.service.spec.ts` pins the OTHER guard, the clear, as genuinely
   * inert for that store: `onIdentityGone()` is gated on `identityScoped()` and does nothing there.
   * The two are separate guards and only one of them is inert; reading that spec as covering both
   * is how the wrong claim got written.)
   */
  protected resetToDefault(): void {
    this.adopt(this.defaultPreset);
  }

  /** Stamp the attribute and refresh the boot mirror. Idempotent. */
  private apply(preset: P): void {
    // Until an authoritative preset arrives, a stamp already on <html> is the BOOT SCRIPT's — made
    // from the mirror, i.e. from what this user actually resolved to last time — and the subject
    // holds only its default. Overwriting a remembered answer with a default is a downgrade in
    // both places it lands: the attribute (the flash) and the mirror (which then reproduces the
    // flash on every later load). BehaviorSubject's replay and a pre-hydration OS-preference change
    // both take this branch; a spec-style host with no attribute (and a first-ever visit, where
    // the boot script has still stamped, but this branch's guard is the attribute, not the
    // mirror) proceeds as before.
    if (!this.hydrated && this.bootStamped) {
      return;
    }
    const resolved = this.resolve(preset, this.media?.matches ?? false);
    if (resolved === null) {
      this.doc.documentElement.removeAttribute(this.cfg.attributeName);
    } else {
      this.doc.documentElement.setAttribute(this.cfg.attributeName, resolved);
    }
    this.emit(this.resolved, resolved);
    // RAW web storage, deliberately, and one of the few sites in src/ that is not on
    // shared/utils/safe-storage. Two reasons, both specific to this key. The mirror's whole job is
    // to be read by the inline boot script in index.html, which runs before any bundle exists, so
    // it must be a plain, un-namespaced, un-wrapped entry that a five-line script can getItem().
    // And the accesses here run from a root service CONSTRUCTOR at boot, ahead of the
    // storage-failure sink being registered, so routing them through the helper would report
    // nothing anyway. The three mirror* helpers above spell every access out in full so this file
    // still ANSWERS the audit grep CLAUDE.md documents for the storage rule — see their note. The
    // known cost: a refusal here never reaches QuotaDiagReporterService's sink, so a user whose
    // mirror cannot be written is invisible to that report; the symptom is a
    // theme/contrast/colour-vision flash on cold load, not data loss. Each call gets its own try,
    // per the same rule — only one of the three branches ever runs.
    // A host with no defaultView has nothing to mirror into, and could not boot a script to read
    // it either. Not an error, and hoisted out of the branches below so each of them can hand the
    // window to one of the mirror* helpers — the `?.` this replaced did the same thing three
    // times over.
    const view = this.doc.defaultView;
    if (view === null) return;
    // AN IDENTITY-SCOPED MIRROR IS WRITTEN WITH ITS OWNER, OR NOT WRITTEN AT ALL. The stamp is what
    // lets the next page load tell this user's remembered answer from the previous occupant's (see
    // onIdentityPresent), and a value written while no identity is in place could never be matched
    // against one — so it is removed rather than left on disk as a mirror nothing can ever claim.
    // In practice that branch is unreachable for the one store that is identity-scoped today:
    // load() needs an identity, onBroadcast refuses one without, and setPreset comes off a screen
    // behind /app. It is here so the invariant is a property of the writer rather than of who
    // happens to call it.
    const owner = this.identityScoped() ? this.activeIdentity : null;
    try {
      if (this.identityScoped() && owner === null) {
        this.mirrorRemove(view);
      } else if (resolved !== null) {
        this.mirrorWrite(view, this.stamped(owner, resolved));
      } else if (preset !== this.defaultPreset) {
        // Resolved off because the user PINNED it off, not because the OS said so. See
        // PINNED_OFF_MIRROR: an absent mirror sends the boot script to its OS branch, which is
        // the flash this user explicitly opted out of.
        this.mirrorWrite(view, this.stamped(owner, PINNED_OFF_MIRROR));
      } else {
        this.mirrorRemove(view);
      }
    } catch {
      // Safari private mode throws on setItem. Losing the flash-prevention mirror is a cosmetic
      // regression on the next cold load; crashing the app over it is not a trade worth making.
    }
  }

  /**
   * The mirror as the boot script found it, out of whichever area `mirrorArea` names — this reads
   * sessionStorage for `display.cvd` and localStorage for the other two, so do not narrow this to
   * one of them. Guarded and normalised to `null`: either area throws in the same modes writing it
   * does (private mode, blocked cookies, a sandboxed iframe — the property GETTER itself throws),
   * and a host with no `defaultView` at all short-circuits the chain to `undefined`, which
   * `bootStamped` would otherwise read as "the boot script had an opinion".
   */
  private readMirror(): string | null {
    try {
      const view = this.doc.defaultView;
      return view === null ? null : this.mirrorRead(view) ?? null;
    } catch {
      return null;
    }
  }
}
